import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Paper, CircularProgress, Alert, Card, CardContent, Grid,
    Chip, Divider, Avatar, Button, Breadcrumbs, Link,
} from '@mui/material';
import {
    Warning as WarningIcon, Business as BusinessIcon,
    AccountBalance as AccountBalanceIcon,
    Person as PersonIcon, SupervisorAccount as SupervisorAccountIcon,
    AssignmentInd as AssignmentIndIcon, HourglassEmpty as HourglassEmptyIcon,
    ArrowBack as ArrowBackIcon, NavigateNext as NavigateNextIcon,
    Groups as GroupsIcon, ManageAccounts as ManageAccountsIcon,
    AdminPanelSettings as AdminPanelSettingsIcon,
} from '@mui/icons-material';

/* ─── flow-table helpers (unchanged) ─────────────────────────────────── */
const LAB_ROLES = ['lab', 'cab'];
const isLabSide  = (r) => LAB_ROLES.some(x => (r || '').toLowerCase().includes(x));
const isDO       = (r) => /\bdo\b/i.test(r || '');
const isJD       = (r) => /\bjd\b/i.test(r || '');
const isAssessor = (r) => /(assessor|lead\s*assessor|\bla\b)/i.test(r || '');
const isWaiting  = (r) => /waiting/i.test(r || '');
const isOvershoot = (row) => (row['TAT Status'] || '').toLowerCase() === 'overshoot';
const isWithinTAT = (row) => (row['TAT Status'] || '').toLowerCase() === 'within tat';
const hoursToDays      = (h) => (Number(h) / 24).toFixed(2);
const hoursToDaysRound = (h) => Math.round(Number(h) / 24);

const getRowBg = (row) => {
    if (isOvershoot(row)) return '#ffebee';
    if (isLabSide(row.Role)) return '#e3f2fd';
    if (isWithinTAT(row)) return '#f1f8e9';
    return '#fafafa';
};
const getRowHoverBg = (row) => {
    if (isOvershoot(row)) return '#ffcdd2';
    if (isLabSide(row.Role)) return '#bbdefb';
    if (isWithinTAT(row)) return '#dcedc8';
    return '#f5f5f5';
};

const normalizeDate = (val) => {
    if (!val) return null;
    if (Array.isArray(val)) {
        // prefer ISO string (contains 'T'), fall back to first item
        return val.find(v => v && String(v).includes('T')) || val[0] || null;
    }
    return val;
};

const formatDate = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    return isNaN(dt) ? '—' : dt.toLocaleDateString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
};

const TATChip = ({ row }) => {
    const status = row['TAT Status'];
    if (!status) return <Typography sx={{ fontSize: '0.7rem', color: '#aaa' }}>—</Typography>;
    const overshoot = isOvershoot(row);
    return (
        <Chip
            label={status}
            size="small"
            icon={overshoot ? <WarningIcon sx={{ fontSize: '12px !important', color: '#fff !important' }} /> : undefined}
            sx={{
                fontSize: '0.68rem', height: 20, fontWeight: 700,
                background: overshoot ? '#b71c1c' : '#2e7d32',
                color: '#fff',
            }}
        />
    );
};

/* ─── level config ────────────────────────────────────────────────────── */
const LEVELS = {
    directors:    { label: 'Directors',    avatarBg: '#1a237e', chipBg: '#e8eaf6', chipColor: '#1a237e', icon: <AdminPanelSettingsIcon fontSize="small" /> },
    jds:          { label: 'JDs',          avatarBg: '#4a148c', chipBg: '#f3e5f5', chipColor: '#4a148c', icon: <ManageAccountsIcon fontSize="small" /> },
    dos:          { label: 'DOs',          avatarBg: '#1b5e20', chipBg: '#e8f5e9', chipColor: '#1b5e20', icon: <PersonIcon fontSize="small" /> },
    applications: { label: 'Applications', avatarBg: '#e65100', chipBg: '#fff3e0', chipColor: '#e65100', icon: <GroupsIcon fontSize="small" /> },
};

/* ─── OfficerCards component ─────────────────────────────────────────── */
const OfficerCards = ({ officers, onSelect, level, loading }) => {
    const cfg = LEVELS[level] || LEVELS.directors;
    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
    if (!officers.length) return <Alert severity="info">No {cfg.label.toLowerCase()} found.</Alert>;
    return (
        <Grid container spacing={2}>
            {officers.map((o, i) => {
                const name = o.DirName || o.name || 'Unknown';
                const oid  = o.OrgId || o.orgId;
                const count = o.appCount ?? 0;
                return (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={oid || i}>
                        <Card
                            onClick={() => onSelect(o)}
                            sx={{
                                cursor: 'pointer',
                                border: `1.5px solid ${cfg.avatarBg}33`,
                                transition: 'all 0.18s',
                                '&:hover': { transform: 'translateY(-2px)', boxShadow: 4, borderColor: cfg.avatarBg },
                            }}
                        >
                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, '&:last-child': { pb: 2 } }}>
                                <Avatar sx={{ background: cfg.avatarBg, width: 40, height: 40, flexShrink: 0 }}>
                                    {cfg.icon}
                                </Avatar>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="subtitle2" fontWeight={700} noWrap title={name} sx={{ fontSize: '0.82rem' }}>
                                        {name}
                                    </Typography>
                                    <Chip
                                        label={`${count} Applications`}
                                        size="small"
                                        sx={{ mt: 0.5, fontSize: '0.65rem', height: 18, background: cfg.chipBg, color: cfg.chipColor, fontWeight: 700 }}
                                    />
                                </Box>
                                <Typography sx={{ color: cfg.avatarBg, fontSize: '1.3rem', fontWeight: 700, opacity: 0.6 }}>›</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                );
            })}
        </Grid>
    );
};

/* ─── ApplicationsTable component ──────────────────────────────────────── */
const ApplicationsTable = ({ apps, onSelect, loading }) => {
    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
    if (!apps.length) return <Alert severity="info">No applications found for this DO.</Alert>;
    return (
        <TableContainer component={Paper} sx={{ maxHeight: 550 }}>
            <Table stickyHeader size="small">
                <TableHead>
                    <TableRow sx={{ '& th': { background: '#1a237e', color: '#fff', fontWeight: 600, fontSize: '0.77rem', whiteSpace: 'nowrap' } }}>
                        <TableCell>#</TableCell>
                        <TableCell>Application No</TableCell>
                        <TableCell>Laboratory Name</TableCell>
                        <TableCell>Lab Type</TableCell>
                        <TableCell>Stage/Status</TableCell>
                        <TableCell>Submit Date</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {apps.map((app, i) => (
                        <TableRow
                            key={app.AppId || app.appId || i}
                            hover
                            onClick={() => onSelect(app)}
                            sx={{ cursor: 'pointer', '&:hover': { background: '#e3f2fd' } }}
                        >
                            <TableCell sx={{ fontSize: '0.75rem' }}>{i + 1}</TableCell>
                            <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#00024c' }}>
                                {app.ApplicationNo || app.applicationNo || '—'}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>{app.LaboratoryName || app.laboratoryName || '—'}</TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>
                                <Chip label={app.LabType || app.labType || '—'} size="small" sx={{ fontSize: '0.65rem', height: 18 }} />
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>{app.Status || app.StageName || app.status || app.stage || '—'}</TableCell>
                            <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                                {(() => {
                                    const d = normalizeDate(app.SubmitDate);
                                    if (!d) return '—';
                                    const dt = new Date(d);
                                    return isNaN(dt) ? String(d) : dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
                                })()}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

/* ─── FlowTable component (existing logic) ──────────────────────────────── */
const FlowTable = ({ data, app }) => {
    const labHours      = data.filter(r => isLabSide(r.Role)).reduce((s, r) => s + (Number(r['no.of hours']) || 0), 0);
    const nablHours     = data.filter(r => isDO(r.Role) || isJD(r.Role) || isAssessor(r.Role)).reduce((s, r) => s + (Number(r['no.of hours']) || 0), 0);
    const waitingHours  = data.filter(r => isWaiting(r.Role)).reduce((s, r) => s + (Number(r['no.of hours']) || 0), 0);
    const totalHours    = labHours + nablHours + waitingHours;
    const doHours       = data.filter(r => isDO(r.Role)).reduce((s, r) => s + (Number(r['no.of hours']) || 0), 0);
    const jdHours       = data.filter(r => isJD(r.Role)).reduce((s, r) => s + (Number(r['no.of hours']) || 0), 0);
    const assessorHours = data.filter(r => isAssessor(r.Role)).reduce((s, r) => s + (Number(r['no.of hours']) || 0), 0);
    const overshootRows = data.filter(r => isOvershoot(r));
    const totalOvershootHours = overshootRows.reduce((s, r) => s + (Number(r['OvershootHours']) || 0), 0);
    const laboratoryName  = app?.LaboratoryName || app?.laboratoryName || '';
    const stageDescription = app?.Status || app?.StageName || app?.stageDescription || '';

    if (!data.length) return <Alert severity="info">No flow data found for this application.</Alert>;

    return (
        <>
            {laboratoryName && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <BusinessIcon sx={{ color: '#00024c', fontSize: 20 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#00024c' }}>{laboratoryName}</Typography>
                    {stageDescription && (
                        <Typography variant="body2" sx={{ color: '#555' }}>(Current stage: {stageDescription})</Typography>
                    )}
                </Box>
            )}

            <Grid container spacing={1.5} sx={{ mb: 2 }}>
                {[
                    { label: 'Total Time',    value: hoursToDaysRound(totalHours),    valueSub: `(${Number(totalHours).toFixed(2)}h)`,    color: '#00024c', bg: '#e8eaf6', icon: <AccountBalanceIcon sx={{ fontSize: 22, color: '#00024c' }} />, sub: 'days' },
                    { label: 'Lab Side',      value: hoursToDaysRound(labHours),      valueSub: `(${Number(labHours).toFixed(2)}h)`,      color: '#1565c0', bg: '#e3f2fd', icon: <BusinessIcon sx={{ fontSize: 22, color: '#1565c0' }} />, sub: 'days' },
                    { label: 'NABL Side',     value: hoursToDaysRound(nablHours),     valueSub: `(${Number(nablHours).toFixed(2)}h)`,     color: '#4527a0', bg: '#ede7f6', icon: <AccountBalanceIcon sx={{ fontSize: 22, color: '#4527a0' }} />, sub: 'days' },
                    { label: 'Waiting Time',  value: hoursToDaysRound(waitingHours),  valueSub: `(${Number(waitingHours).toFixed(2)}h)`,  color: '#e65100', bg: '#fff3e0', icon: <HourglassEmptyIcon sx={{ fontSize: 22, color: '#e65100' }} />, sub: 'days' },
                    { label: 'TAT Overshoot', value: overshootRows.length,            valueSub: `${Number(totalOvershootHours).toFixed(2)}h over`, color: '#b71c1c', bg: '#ffebee', icon: <WarningIcon sx={{ fontSize: 22, color: '#b71c1c' }} />, sub: 'stages' },
                ].map(item => (
                    <Grid item xs={12} sm={6} md={2.4} key={item.label}>
                        <Card sx={{ border: `1px solid ${item.color}44`, background: item.bg }}>
                            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                {item.icon}
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 700, color: item.color, lineHeight: 1 }}>{item.value}</Typography>
                                        <Typography variant="caption" sx={{ color: item.color, fontWeight: 600, fontSize: '0.65rem' }}>{item.sub}</Typography>
                                    </Box>
                                    <Typography variant="caption" sx={{ color: item.color, display: 'block', fontWeight: 600, mt: 0.2, fontSize: '0.7rem' }}>{item.label}</Typography>
                                    <Typography variant="caption" sx={{ color: item.color, opacity: 0.7, fontSize: '0.62rem' }}>{item.valueSub}</Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Grid container spacing={1.5} sx={{ mb: 3 }}>
                {[
                    { label: 'DO Days',       value: hoursToDaysRound(doHours),       valueSub: `(${Number(doHours).toFixed(2)}h)`,       color: '#1b5e20', bg: '#e8f5e9', icon: <PersonIcon sx={{ fontSize: 20, color: '#1b5e20' }} />, sub: 'days' },
                    { label: 'JD Days',       value: hoursToDaysRound(jdHours),       valueSub: `(${Number(jdHours).toFixed(2)}h)`,       color: '#e65100', bg: '#fff3e0', icon: <SupervisorAccountIcon sx={{ fontSize: 20, color: '#e65100' }} />, sub: 'days' },
                    { label: 'Assessor Days', value: hoursToDaysRound(assessorHours), valueSub: `(${Number(assessorHours).toFixed(2)}h)`, color: '#4a148c', bg: '#f3e5f5', icon: <AssignmentIndIcon sx={{ fontSize: 20, color: '#4a148c' }} />, sub: 'days' },
                ].map(item => (
                    <Grid item xs={12} sm={4} key={item.label}>
                        <Card sx={{ border: `1px solid ${item.color}44`, background: item.bg }}>
                            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                {item.icon}
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 700, color: item.color, lineHeight: 1 }}>{item.value}</Typography>
                                        <Typography variant="caption" sx={{ color: item.color, fontWeight: 600, fontSize: '0.65rem' }}>{item.sub}</Typography>
                                    </Box>
                                    <Typography variant="caption" sx={{ color: item.color, display: 'block', fontWeight: 600, mt: 0.2, fontSize: '0.7rem' }}>{item.label}</Typography>
                                    <Typography variant="caption" sx={{ color: item.color, opacity: 0.7, fontSize: '0.62rem' }}>{item.valueSub}</Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Box sx={{ display: 'flex', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
                {[
                    { bg: '#ffebee', border: '#b71c1c', label: 'TAT Overshoot' },
                    { bg: '#e3f2fd', border: '#1565c0', label: 'Lab Side (no TAT limit)' },
                    { bg: '#f1f8e9', border: '#558b2f', label: 'Within TAT' },
                    { bg: '#fafafa', border: '#bbb',    label: 'No TAT defined' },
                ].map(l => (
                    <Box key={l.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 14, height: 14, background: l.bg, border: `1.5px solid ${l.border}`, borderRadius: 0.5 }} />
                        <Typography variant="caption" sx={{ fontSize: '0.68rem', color: '#555' }}>{l.label}</Typography>
                    </Box>
                ))}
            </Box>

            <TableContainer component={Paper} sx={{ maxHeight: 600, overflowX: 'auto' }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow sx={{ '& th': { background: '#1a237e', color: '#fff', fontWeight: 600, fontSize: '0.77rem', whiteSpace: 'nowrap' } }}>
                            <TableCell>#</TableCell>
                            <TableCell>Details</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell>Start Date</TableCell>
                            <TableCell>End Date</TableCell>
                            <TableCell>Actual Hours</TableCell>
                            <TableCell sx={{ background: '#4a148c !important' }}>Allowed Hours</TableCell>
                            <TableCell sx={{ background: '#4a148c !important' }}>TAT Status</TableCell>
                            <TableCell sx={{ background: '#b71c1c !important' }}>Overshoot Hours</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.map((row, i) => {
                            const labSide    = isLabSide(row.Role);
                            const overshoot  = isOvershoot(row);
                            const actualHrs  = row['no.of hours'];
                            const allowedHrs = row['AllowedHours'];
                            const overshootHrs = row['OvershootHours'];
                            return (
                                <TableRow key={i} sx={{ background: getRowBg(row), '&:hover': { background: getRowHoverBg(row) } }}>
                                    <TableCell sx={{ fontSize: '0.75rem' }}>{i + 1}</TableCell>
                                    <TableCell sx={{ fontSize: '0.75rem', maxWidth: 200 }}>{row.Details || '—'}</TableCell>
                                    <TableCell sx={{ fontSize: '0.75rem' }}>
                                        <Chip label={row.Role || '—'} size="small" sx={{ fontSize: '0.68rem', height: 20, fontWeight: 600, background: labSide ? '#1565c0' : '#37474f', color: '#fff' }} />
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{formatDate(row.StageStartDate)}</TableCell>
                                    <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{formatDate(row.StageEndDate)}</TableCell>
                                    <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, color: overshoot ? '#b71c1c' : labSide ? '#1565c0' : '#37474f' }}>
                                        {actualHrs != null && actualHrs !== ''
                                            ? <><span>{hoursToDays(actualHrs)} days</span><br /><span style={{ fontWeight: 400, fontSize: '0.68rem', opacity: 0.8 }}>({Number(actualHrs).toFixed(2)}h)</span></>
                                            : '—'}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.75rem', color: '#555' }}>
                                        {allowedHrs != null
                                            ? <><span>{hoursToDays(allowedHrs)} days</span><br /><span style={{ fontSize: '0.68rem', opacity: 0.75 }}>({Number(allowedHrs).toFixed(2)}h)</span></>
                                            : '—'}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.75rem' }}><TATChip row={row} /></TableCell>
                                    <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#b71c1c' }}>
                                        {overshoot && overshootHrs != null && Number(overshootHrs) > 0
                                            ? <><span>+{hoursToDays(overshootHrs)} days</span><br /><span style={{ fontWeight: 400, fontSize: '0.68rem', opacity: 0.8 }}>(+{Number(overshootHrs).toFixed(2)}h)</span></>
                                            : <Typography sx={{ fontSize: '0.75rem', color: '#aaa' }}>—</Typography>
                                        }
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

/* ─── main page ───────────────────────────────────────────────────────── */
const LabFlowDetails = () => {
    const [view, setView] = useState('directors'); // directors | jds | dos | applications | flow
    const [selectedDirector, setSelectedDirector] = useState(null);
    const [selectedJD, setSelectedJD]             = useState(null);
    const [selectedDO, setSelectedDO]             = useState(null);
    const [selectedApp, setSelectedApp]           = useState(null);

    const [directors, setDirectors]     = useState([]);
    const [jds, setJDs]                 = useState([]);
    const [dos, setDOs]                 = useState([]);
    const [appList, setAppList]         = useState([]);
    const [flowData, setFlowData]       = useState([]);

    const [loading, setLoading] = useState(false);
    const [error, setError]     = useState(null);

    const orgId      = localStorage.getItem('orgId');
    const userTypeId = JSON.parse(localStorage.getItem('userData') || '{}').userTypeId || '8';

    const authHeader = () => {
        const token = localStorage.getItem('authToken');
        return { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) };
    };

    const apiFetch = async (endpoint, body) => {
        const r = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
            method: 'POST', headers: authHeader(), body: JSON.stringify(body),
        });
        const j = await r.json();
        if (!j.success) throw new Error(j.message || 'Request failed');
        return j.data || [];
    };

    useEffect(() => {
        (async () => {
            setLoading(true); setError(null);
            try {
                const data = await apiFetch('/ceo/flow-hierarchy/directors', { orgId, userTypeId });
                setDirectors(data);
            } catch (e) { setError(e.message); }
            finally { setLoading(false); }
        })();
    }, []);

    const handleDirectorClick = async (director) => {
        setSelectedDirector(director);
        setView('jds');
        setLoading(true); setError(null);
        try {
            const data = await apiFetch('/ceo/flow-hierarchy/jds', { orgId: director.OrgId || director.orgId });
            setJDs(data);
        } catch (e) { setError(e.message); }
        finally { setLoading(false); }
    };

    const handleJDClick = async (jd) => {
        setSelectedJD(jd);
        setView('dos');
        setLoading(true); setError(null);
        try {
            const data = await apiFetch('/ceo/flow-hierarchy/dos', { orgId: jd.OrgId || jd.orgId });
            setDOs(data);
        } catch (e) { setError(e.message); }
        finally { setLoading(false); }
    };

    const handleDOClick = async (doOfficer) => {
        setSelectedDO(doOfficer);
        setView('applications');
        setLoading(true); setError(null);
        try {
            const data = await apiFetch('/ceo/flow-hierarchy/applications', { orgId: doOfficer.id || doOfficer.orgId });
            setAppList(data);
        } catch (e) { setError(e.message); }
        finally { setLoading(false); }
    };

    const handleAppClick = async (app) => {
        setSelectedApp(app);
        setView('flow');
        setLoading(true); setError(null);
        try {
            const data = await apiFetch('/ceo/lab-flow-details', { appId: app.AppId || app.appId });
            const filtered = data.filter(r => r['no.of hours'] !== null && r['no.of hours'] !== undefined && r['no.of hours'] !== '');
            setFlowData(filtered);
        } catch (e) { setError(e.message); }
        finally { setLoading(false); }
    };

    const goToView = (target) => {
        if (target === 'directors') {
            setView('directors');
            setSelectedDirector(null); setSelectedJD(null); setSelectedDO(null); setSelectedApp(null);
            setJDs([]); setDOs([]); setAppList([]); setFlowData([]);
        } else if (target === 'jds') {
            setView('jds');
            setSelectedJD(null); setSelectedDO(null); setSelectedApp(null);
            setDOs([]); setAppList([]); setFlowData([]);
        } else if (target === 'dos') {
            setView('dos');
            setSelectedDO(null); setSelectedApp(null);
            setAppList([]); setFlowData([]);
        } else if (target === 'applications') {
            setView('applications');
            setSelectedApp(null);
            setFlowData([]);
        }
        setError(null);
    };

    /* breadcrumb items */
    const crumbs = [
        { label: 'Directors', target: 'directors', active: view === 'directors' },
        ...(selectedDirector ? [{ label: selectedDirector.Name || selectedDirector.name, target: 'jds', active: view === 'jds' }] : []),
        ...(selectedJD       ? [{ label: selectedJD.Name || selectedJD.name,             target: 'dos', active: view === 'dos' }] : []),
        ...(selectedDO       ? [{ label: selectedDO.Name || selectedDO.name,             target: 'applications', active: view === 'applications' }] : []),
        ...(selectedApp      ? [{ label: selectedApp.ApplicationNo || selectedApp.applicationNo || 'Application', target: 'flow', active: view === 'flow' }] : []),
    ];

    const sectionTitles = {
        directors:    'Directors',
        jds:          `JDs under ${selectedDirector?.Name || selectedDirector?.name || ''}`,
        dos:          `DOs under ${selectedJD?.Name || selectedJD?.name || ''}`,
        applications: `Applications — ${selectedDO?.Name || selectedDO?.name || ''}`,
        flow:         `Flow Details — ${selectedApp?.ApplicationNo || selectedApp?.applicationNo || ''}`,
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Page header */}
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#00024c', mb: 0.5 }}>
                Application Flow Details
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                Drill down: Director → JD → DO → Application → Stage-wise flow
            </Typography>

            {/* Breadcrumbs */}
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 1.5 }}>
                {crumbs.map((c, i) =>
                    c.active
                        ? <Typography key={i} color="text.primary" fontWeight={700} fontSize="0.82rem">{c.label}</Typography>
                        : <Link key={i} component="button" variant="body2" underline="hover" onClick={() => goToView(c.target)} sx={{ fontSize: '0.82rem', cursor: 'pointer' }}>{c.label}</Link>
                )}
            </Breadcrumbs>

            {/* Back button */}
            {view !== 'directors' && (
                <Button
                    startIcon={<ArrowBackIcon />}
                    size="small"
                    onClick={() => {
                        const prev = { jds: 'directors', dos: 'jds', applications: 'dos', flow: 'applications' };
                        goToView(prev[view]);
                    }}
                    sx={{ mb: 2, textTransform: 'none' }}
                >
                    Back
                </Button>
            )}

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Divider sx={{ mb: 2 }} />

            {/* Section title */}
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#00024c', mb: 2 }}>
                {sectionTitles[view]}
            </Typography>

            {/* Views */}
            {view === 'directors' && (
                <OfficerCards officers={directors} onSelect={handleDirectorClick} level="directors" loading={loading} />
            )}

            {view === 'jds' && (
                <OfficerCards officers={jds} onSelect={handleJDClick} level="jds" loading={loading} />
            )}

            {view === 'dos' && (
                <OfficerCards officers={dos} onSelect={handleDOClick} level="dos" loading={loading} />
            )}

            {view === 'applications' && (
                <ApplicationsTable apps={appList} onSelect={handleAppClick} loading={loading} />
            )}

            {view === 'flow' && !loading && (
                <FlowTable data={flowData} app={selectedApp} />
            )}

            {view === 'flow' && loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            )}
        </Box>
    );
};

export default LabFlowDetails;
